import { ChangeEvent, useRef, useState } from 'react'

import { useFileSelect } from '../../../../shared/hooks/useFileSelect'
import { useFileUpload } from '../../../../shared/hooks/useFileUpload'
import { FeatureFlag, useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { startTimer, trackEvent } from '@/utils/analytics/useAnalytics'
import { useTranslation } from 'react-i18next'

import { useApolloClient } from '@apollo/client'
import { AnimatePresence } from 'framer-motion'

import {
  Box,
  Button,
  MultilineTextInput,
  PaperClipIcon,
  UtilityButton,
} from '@hivewatch/dark'

import {
  IncidentDocument,
  useCreateIncidentNoteMediaMutation,
  useCreateIncidentNoteMutation,
} from '@/graphql/generated/hooks'
import { BUTTON_PRESS } from '@/utils/analytics'

import {
  ALLOWED_EXTENSIONS,
  MAX_FILES,
} from '../../../../shared/constants/addNoteConstants'
import { MixpanelDataIProps } from '../../../../shared/types/types'
import { updateIncidentNoteMediaCache } from '../../../../shared/utils/incidentNoteCache'
import { SelectedFilesPreview } from './SelectedFilesPreview'

type ColorScheme = 'dark' | 'light'

interface AddNoteIProps {
  incidentId: string
  mixpanelData?: MixpanelDataIProps
  onActionComplete?: () => void
  colorScheme?: ColorScheme
}

export const AddNote = ({
  incidentId,
  mixpanelData,
  onActionComplete,
  colorScheme = 'dark',
}: AddNoteIProps) => {
  const { t } = useTranslation()
  const { shouldEnableIncidentNote } = usePermissions()
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const flags = useFeatureFlags()
  const isAttachMediaToIncidentNoteEnabled =
    flags?.[FeatureFlag.attachMediaToIncidentNote]

  const allowedExtensions = ALLOWED_EXTENSIONS.join(',')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const openFileDialog = () => {
    fileInputRef.current?.click()
  }

  const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event)
  }

  const [createIncidentNote] = useCreateIncidentNoteMutation()
  const [createIncidentNoteMedia] = useCreateIncidentNoteMediaMutation()
  const apolloClient = useApolloClient()

  const {
    handleFileSelect,
    removeFile,
    previewFile,
    selectedFiles,
    setPreviewFile,
    setSelectedFiles,
  } = useFileSelect()

  const ADD_NOTE_BUTTON_EVENT_NAME = `${BUTTON_PRESS} Add Note - Post`

  const onCancel = () => {
    trackEvent(`${BUTTON_PRESS} Add Note - Cancel`, mixpanelData)
    onActionComplete?.()
  }

  const handlePostComplete = () => {
    setComment('')
    trackEvent(ADD_NOTE_BUTTON_EVENT_NAME, mixpanelData)
  }

  const { showError } = useToasts()
  const { handleFileUpload } = useFileUpload()

  const onSubmit = async () => {
    startTimer(ADD_NOTE_BUTTON_EVENT_NAME)
    setIsSubmitting(true)

    let isUploadSuccessful = false

    try {
      if (selectedFiles.length > 0) {
        const uploadResults = await handleFileUpload(selectedFiles)

        // useFileUpload reports failures by resolving with undefined, so a
        // fulfilled status alone doesn't mean the upload succeeded — only
        // truthy values are real uploads the media mutation may reference.
        const fileIds = uploadResults.flatMap((result) =>
          result.status === 'fulfilled' && result.value ? [result.value] : []
        )
        isUploadSuccessful = fileIds.length > 0

        if (isUploadSuccessful) {
          // The note refetches on its own mutation so it becomes visible even
          // if the media step below fails.
          const { data } = await createIncidentNote({
            variables: { input: { incidentId, message: comment } },
            refetchQueries: [
              { query: IncidentDocument, variables: { id: incidentId } },
            ],
          })

          try {
            const { data: mediaData } = await createIncidentNoteMedia({
              variables: {
                input: {
                  incidentNoteId: data?.createIncidentNote?.incidentNote?.id,
                  media: fileIds,
                },
              },
              refetchQueries: [
                { query: IncidentDocument, variables: { id: incidentId } },
              ],
            })

            updateIncidentNoteMediaCache(
              apolloClient.cache,
              data?.createIncidentNote?.incidentNote?.id,
              mediaData?.createIncidentNoteMedia?.media
            )
          } catch {
            // The note already exists server-side. Clear the form either way so
            // a retry can't post a duplicate note; tell the operator only the
            // attachments were lost.
            showError(t('incidents:add_note_media_attach_failed'))
          }
          setSelectedFiles([])
          handlePostComplete()
        } else {
          showError(t('incidents:add_note_file_upload_failed'))
        }
      } else {
        await createIncidentNote({
          variables: { input: { incidentId, message: comment } },
          refetchQueries: [
            { query: IncidentDocument, variables: { id: incidentId } },
          ],
        })

        handlePostComplete()
      }
    } catch {
      showError()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClick = () => {
    trackEvent(`${BUTTON_PRESS} Add Note - Comment Textbox`, mixpanelData)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (comment?.trim() && !isSubmitting && shouldEnableIncidentNote) {
        onSubmit()
      }
    }
  }

  const isDark = colorScheme === 'dark'
  const labelColor = isDark ? 'text.primary' : 'light.text.primary'

  return (
    <Box display='flex' flexDirection='column' gap='16px' px='24px' py='16px'>
      <Box
        color={labelColor}
        fontFamily="'Soehne Kraftig', sans-serif"
        fontSize='20px'
        fontWeight='500'
        lineHeight='28px'
      >
        {t('common:add_note')}
      </Box>

      <Box
        data-testid='incidentDrawer_addNote_textarea'
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <MultilineTextInput
          colorScheme={colorScheme}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('incidents:add_note_placeholder')}
          value={comment}
        />
      </Box>

      {isAttachMediaToIncidentNoteEnabled && selectedFiles.length > 0 && (
        <SelectedFilesPreview
          previewFile={previewFile}
          removeFile={removeFile}
          selectedFiles={selectedFiles}
          setPreviewFile={setPreviewFile}
        />
      )}

      <AnimatePresence exitBeforeEnter>
        <Box
          alignItems='center'
          display='flex'
          justifyContent={
            isAttachMediaToIncidentNoteEnabled ? 'space-between' : 'flex-end'
          }
          key='createIncidentNoteButtons'
        >
          {isAttachMediaToIncidentNoteEnabled && (
            <>
              <UtilityButton
                colorScheme={colorScheme}
                data-testid='incidentDrawer_addNote_attachMediaButton'
                disabled={selectedFiles.length >= MAX_FILES}
                icon={<PaperClipIcon colorScheme={colorScheme} size={18} />}
                label={t('incidents:add_note_attach_label')}
                onClick={openFileDialog}
                tooltipLabel={t('incidents:attach_media')}
              />
              <input
                accept={allowedExtensions}
                className='hidden'
                multiple
                onChange={onFileInputChange}
                onClick={(event) => {
                  if (event.currentTarget) {
                    event.currentTarget.value = ''
                  }
                }}
                ref={fileInputRef}
                type='file'
              />
            </>
          )}
          <Box display='flex' gap='8px'>
            <Button
              colorScheme={colorScheme}
              data-testid='incidentDrawer_addNote_cancel'
              onClick={onCancel}
              size='md'
              variant='secondary'
            >
              {t('common:cancel')}
            </Button>
            <Button
              colorScheme={colorScheme}
              data-testid='incidentDrawer_addNote_post'
              disabled={!shouldEnableIncidentNote || comment === ''}
              isLoading={isSubmitting}
              onClick={onSubmit}
              size='md'
              variant='primary'
            >
              {t('incidents:add_note_post')}
            </Button>
          </Box>
        </Box>
      </AnimatePresence>
    </Box>
  )
}
